<template>
    <v-row>
        <v-col cols="12">
            <h2 class="mt-5 mb-4">{{ title }}</h2>

            <v-select
                v-model="details.nationality"
                :items="nationalityChoices"
                item-text="name"
                item-value="id"
                label="Nationality"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.nationality"
                :error="getError(`applicants.${index}.nationality`)"
                :error-messages="getErrorMessages(`applicants.${index}.nationality`)"
                @input="removeErrorMessage(`applicants.${index}.nationality`)"
            ></v-select>

            <v-select
                v-model="details.ethnicity"
                :items="ethnicityChoices"
                item-text="name"
                item-value="id"
                label="Ethnicity"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.ethnicity"
                :error="getError(`applicants.${index}.ethnicity`)"
                :error-messages="getErrorMessages(`applicants.${index}.ethnicity`)"
                @input="removeErrorMessage(`applicants.${index}.ethnicity`)"
            ></v-select>

            <v-select
                v-model="details.religion"
                :items="religionChoices"
                item-text="name"
                item-value="id"
                label="Religion"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.religion"
                :error="getError(`applicants.${index}.religion`)"
                :error-messages="getErrorMessages(`applicants.${index}.religion`)"
                @input="removeErrorMessage(`applicants.${index}.religion`)"
            ></v-select>

            <v-select
                v-model="details.sexuality"
                :items="sexualityChoices"
                item-text="name"
                item-value="id"
                label="Sexuality"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :rules="rules.sexuality"
                :error="getError(`applicants.${index}.sexuality`)"
                :error-messages="getErrorMessages(`applicants.${index}.sexuality`)"
                @input="removeErrorMessage(`applicants.${index}.sexuality`)"
            ></v-select>

            <legend class="v-label theme--light mt-8">
                Are you an employee or related to a current
                or former committee or board member?
            </legend>
            <v-radio-group
                class="mt-1"
                v-model="details.relatedToHAWorker"
                row
                :error="getError(`applicants.${index}.relatedToHAWorker`)"
                :error-messages="getErrorMessages(`applicants.${index}.relatedToHAWorker`)"
                @input="removeErrorMessage(`applicants.${index}.relatedToHAWorker`)"
                @change="relatedToHAWorkerChanged"
            >
                <v-radio
                    label="Yes"
                    :value="true"
                ></v-radio>
                <v-radio
                    label="No"
                    :value="false"
                ></v-radio>
            </v-radio-group>

            <v-select
                v-if="details.relatedToHAWorker"
                v-model="details.relationshipToHAWorker"
                :items="relationshipToHAWorkerChoices"
                item-text="name"
                item-value="id"
                label="Relationship to HA worker"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :error="getError(`applicants.${index}.relationshipToHAWorker`)"
                :error-messages="getErrorMessages(`applicants.${index}.relationshipToHAWorker`)"
                @input="removeErrorMessage(`applicants.${index}.relationshipToHAWorker`)"
            ></v-select>

            <legend class="v-label theme--light mt-2">
                Do you consider anyone in your household to be disabled?
            </legend>
            <v-radio-group
                class="mt-1"
                v-model="details.anyoneInHouseholdDisabled"
                row
                :error="getError(`applicants.${index}.anyoneInHouseholdDisabled`)"
                :error-messages="getErrorMessages(`applicants.${index}.anyoneInHouseholdDisabled`)"
                @input="removeErrorMessage(`applicants.${index}.anyoneInHouseholdDisabled`)"
            >
                <v-radio
                    label="Yes"
                    :value="true"
                ></v-radio>
                <v-radio
                    label="No"
                    :value="false"
                ></v-radio>
            </v-radio-group>

            <legend class="v-label theme--light mt-2">
                Registered Disabled?
            </legend>
            <v-radio-group
                class="mt-1"
                v-model="details.registeredDisabled"
                row
                :error="getError(`applicants.${index}.registeredDisabled`)"
                :error-messages="getErrorMessages(`applicants.${index}.registeredDisabled`)"
                @input="removeErrorMessage(`applicants.${index}.registeredDisabled`)"
            >
                <v-radio
                    label="Yes"
                    :value="true"
                ></v-radio>
                <v-radio
                    label="No"
                    :value="false"
                ></v-radio>
            </v-radio-group>

            <legend class="v-label theme--light mt-2">
                Wheelchair User?
            </legend>
            <v-radio-group
                class="mt-1"
                v-model="details.wheelchairUser"
                row
                :error="getError(`applicants.${index}.wheelchairUser`)"
                :error-messages="getErrorMessages(`applicants.${index}.wheelchairUser`)"
                @input="removeErrorMessage(`applicants.${index}.wheelchairUser`)"
            >
                <v-radio
                    label="Yes"
                    :value="true"
                ></v-radio>
                <v-radio
                    label="No"
                    :value="false"
                ></v-radio>
            </v-radio-group>

            <v-textarea
                v-model="details.accessibilityNeeds"
                label="Accessibility Needs"
                outlined
                required
                hide-details="auto"
                class="rounded-0 mb-4 v-input--required"
                :error="getError(`applicants.${index}.accessibilityNeeds`)"
                :error-messages="getErrorMessages(`applicants.${index}.accessibilityNeeds`)"
                @input="removeErrorMessage(`applicants.${index}.accessibilityNeeds`)"
            ></v-textarea>
        </v-col>
    </v-row>
</template>

<script>
import { defineComponent, ref, onMounted, watch } from '@vue/composition-api';
import HelperServices from "../../../services/HelperServices";
import {forEach, has, isEmpty} from "lodash";

export default defineComponent({
    name: 'Equality',
    props: [
        'accessToken',
        'index',
        'title',
        'getError',
        'getErrorMessages',
        'removeErrorMessage',
        'loggedInProfile',
        'value',
        'formDisabled'
    ],
    setup(props, { emit }) {
        const accessToken = ref(null);
        const details = ref({
            nationality: null,
            ethnicity: null,
            religion: null,
            sexuality: null,
            relatedToHAWorker: null,
            relationshipToHAWorker: null,
            anyoneInHouseholdDisabled: null,
            registeredDisabled: null,
            wheelchairUser: null,
            accessibilityNeeds: null
        });

        const nationalityChoices = ref([]);
        const ethnicityChoices = ref([]);
        const religionChoices = ref([]);
        const sexualityChoices = ref([]);
        const relationshipToHAWorkerChoices = ref([]);

        const rules = ref({
            nationality : [
                v => !!v || 'Nationality is required',
            ],
            ethnicity: [
                v => !!v || 'Ethnicity is required',
            ],
            religion: [
                v => !!v || 'Religion is required',
            ],
            sexuality: [
                v => !!v || 'Sexuality is required',
            ],
        });

        function relatedToHAWorkerChanged(related) {
            if(related === false) {
                details.value.relationshipToHAWorker = null;
            }
        }

        function setFields() {
            let currentFields = {};
            forEach(details.value, function(detValue, detKey) {
                if(has(props, 'value.' + detKey)) {
                    currentFields[detKey] = props.value[detKey];
                } else {
                    currentFields[detKey] = detValue;
                }
            });
            details.value = currentFields;
        }

        onMounted(() => {
            init();
        });

        function init() {
            if(props.accessToken !== '') {
                accessToken.value = props.accessToken;
            }
            Promise
                .all([
                    fetchNationalityChoices(),
                    fetchEthnicityChoices(),
                    fetchReligionChoices(),
                    fetchSexualityChoices(),
                    fetchRelationshipStatusChoices()
                ])
                .then(() => {
                    setFields();
                    emit("input", props.index, details.value);
                    emit('toggleLoading', false);
                });
        }

        function fetchNationalityChoices() {
            return new Promise(function(resolve, reject) {
                emit('toggleLoading', true);
                HelperServices.getNationalityChoices()
                    .then(function (response) {
                        if(response.data.data) {
                            nationalityChoices.value = response.data.data;
                        }
                        resolve(response);
                    })
                    .catch(function (error) {
                        reject(error)
                    })
                    .then(function () {
                        emit('toggleLoading', false);
                    });
            });
        }

        function fetchEthnicityChoices() {
            return new Promise(function(resolve, reject) {
                emit('toggleLoading', true);
                HelperServices.getEthnicityChoices()
                    .then(function (response) {
                        if(response.data.data) {
                            ethnicityChoices.value = response.data.data;
                        }
                        resolve(response);
                    })
                    .catch(function (error) {
                        reject(error)
                    })
                    .then(function () {
                        emit('toggleLoading', false);
                    });
            });
        }

        function fetchReligionChoices() {
            return new Promise(function(resolve, reject) {
                emit('toggleLoading', true);
                HelperServices.getReligionChoices()
                    .then(function (response) {
                        if(response.data.data) {
                            religionChoices.value = response.data.data;
                        }
                        resolve(response);
                    })
                    .catch(function (error) {
                        reject(error)
                    })
                    .then(function () {
                        emit('toggleLoading', false);
                    });
            });
        }

        function fetchSexualityChoices() {
            return new Promise(function(resolve, reject) {
                emit('toggleLoading', true);
                HelperServices.getSexualityChoices()
                    .then(function (response) {
                        if(response.data.data) {
                            sexualityChoices.value = response.data.data;
                        }
                        resolve(response);
                    })
                    .catch(function (error) {
                        reject(error)
                    })
                    .then(function () {
                        emit('toggleLoading', false);
                    });
            });
        }

        function fetchRelationshipStatusChoices() {
            return new Promise(function(resolve, reject) {
                emit('toggleLoading', true);
                HelperServices.getRelationshipStatusChoices()
                    .then(function (response) {
                        if(response.data.data) {
                            relationshipToHAWorkerChoices.value = response.data.data;
                        }
                        resolve(response);
                    })
                    .catch(function (error) {
                        reject(error)
                    })
                    .then(function () {
                        emit('toggleLoading', false);
                    });
            });
        }

        watch(details, (newData, oldData) => {
            emit("input", props.index, newData);
        }, {
            deep: true
        });

        return {
            details,
            rules,
            nationalityChoices,
            ethnicityChoices,
            religionChoices,
            sexualityChoices,
            relationshipToHAWorkerChoices,
            relatedToHAWorkerChanged
        }
    },
})
</script>

<style scoped>
    legend{
        line-height: normal;
    }
</style>
